import { create } from 'zustand';
import { en, type MessageKey } from './en';
import { es } from './es';

export type Lang = 'en' | 'es';
const STORAGE_KEY = 'epb.lang';
const tables: Record<Lang, Record<MessageKey, string>> = { en, es };

/** Values that are legitimately identical in both languages (brand names, format names, cognates). */
export const SAME_IN_BOTH: ReadonlySet<MessageKey> = new Set<MessageKey>([
  'addColour', // M22: "Color" is the same word in Spanish
  'limitRadial',
  'appName',
  'appShort',
  'panelHsl',
  'panelColor',
  'aspectOriginal',
  'bandMagenta',
  'curveMaster',
  'addRadial',
  'panelLut',
  'contactA4',
  'saveInstagram',
  'saveWhatsapp',
  'blendNormal',
  'blendColor',
  'globalSwatch',
  'hex',
]);

function initialLang(): Lang {
  try {
    const v = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (v === 'en' || v === 'es') return v;
  } catch {
    /* storage blocked: fall through */
  }
  const nav = globalThis.navigator?.language ?? 'en';
  return nav.toLowerCase().startsWith('es') ? 'es' : 'en';
}

type LangState = { lang: Lang; setLang: (l: Lang) => void };
export const useLang = create<LangState>((set) => ({
  lang: initialLang(),
  setLang: (lang) => {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    set({ lang });
  },
}));

/** Translate with `{name}` placeholders. Pure: usable outside React. */
export function translate(lang: Lang, key: MessageKey, vars?: Record<string, string | number>): string {
  let s = tables[lang][key];
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** React hook: re-renders on language change. */
export function useT() {
  const lang = useLang((s) => s.lang);
  return (key: MessageKey, vars?: Record<string, string | number>) => translate(lang, key, vars);
}

export { en, es };
export type { MessageKey };
